import { BASE_URL, request } from "../../../api/api";
import type {
  WompiPaymentLinkRequest,
  WompiAmortizationPaymentRequest,
  WompiProviderPaymentRequest,
  WompiPaymentResponse,
  WompiApiResponse,
  WompiErrorResponse,
  WompiWebhookPayload,
  WompiWebhookResponse,
} from "../types/types";
import axios from "axios";

export class WompiService {
  public static async createPaymentLink(
    data: WompiPaymentLinkRequest
  ): Promise<WompiPaymentResponse> {
    try {
      const response = await request<WompiApiResponse>(
        `${BASE_URL}/api/WompiPayment/create-payment-link`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          data,
        }
      );

      // Extract the payment URL with proper priority
      const paymentUrl = response.urlEnlace || response.paymentUrl || response.url;
      
      if (!paymentUrl) {
        console.error("No payment URL found in response:", response);
        return {
          success: false,
          error: "No se recibió URL de pago válida desde Wompi"
        };
      }

      return {
        success: true,
        paymentUrl: paymentUrl,
      };
    } catch (error: unknown) {
      console.error("Error creating Wompi payment link:", error);

      let errorMessage = "Error creating payment link";

      if (axios.isAxiosError(error)) {
        const errorData = error.response?.data as WompiErrorResponse;
        errorMessage = errorData?.message || errorData?.error || error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  public static async createPaymentLinkForAmortization(
    data: WompiAmortizationPaymentRequest
  ): Promise<WompiPaymentResponse> {
    try {
      const response = await request<WompiApiResponse>(
        `${BASE_URL}/api/WompiPayment/create-payment-link-for-amortization`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          data,
        }
      );

      // Extract the payment URL with proper priority
      const paymentUrl = response.urlEnlace || response.paymentUrl || response.url;
      
      if (!paymentUrl) {
        console.error("No payment URL found in amortization response:", response);
        return {
          success: false,
          error: "No se recibió URL de pago válida desde Wompi"
        };
      }

      return {
        success: true,
        paymentUrl: paymentUrl,
      };
    } catch (error: unknown) {
      console.error("Error creating Wompi amortization payment link:", error);

      let errorMessage = "Error creating payment link";

      if (axios.isAxiosError(error)) {
        const errorData = error.response?.data as WompiErrorResponse;
        errorMessage = errorData?.message || errorData?.error || error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  public static async createWithProvider(
    data: WompiProviderPaymentRequest
  ): Promise<WompiPaymentResponse> {
    try {
      const response = await request<WompiApiResponse>(
        `${BASE_URL}/api/WompiPayment/create-with-provider`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          data,
        }
      );

      return {
        success: true,
        paymentUrl: response.urlEnlace || response.paymentUrl || response.url,
      };
    } catch (error: unknown) {
      console.error("Error creating Wompi provider payment:", error);

      let errorMessage = "Error creating payment link";

      if (axios.isAxiosError(error)) {
        const errorData = error.response?.data as WompiErrorResponse;
        errorMessage = errorData?.message || errorData?.error || error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Handle Wompi webhook
   */
  public static async handleWebhook(payload: WompiWebhookPayload): Promise<WompiWebhookResponse> {
    try {
      const response = await request<WompiWebhookResponse>(
        `${BASE_URL}/api/WompiPayment/webhook`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          data: payload,
        }
      );

      return response;
    } catch (error: unknown) {
      console.error("Error handling Wompi webhook:", error);
      return {
        processed: false,
        error: error instanceof Error ? error.message : "Unknown error"
      };
    }
  }

  /**
   * Test authentication with Wompi
   */
  public static async testAuth(): Promise<{ success: boolean; message?: string }> {
    try {
      await request(`${BASE_URL}/api/WompiPayment/test-auth`, {
        method: "GET"
      });

      return { success: true, message: "Authentication successful" };
    } catch (error: unknown) {
      console.error("Error testing Wompi auth:", error);
      return { 
        success: false, 
        message: error instanceof Error ? error.message : "Authentication failed" 
      };
    }
  }

  /**
   * Get payment status for amortization
   */
  public static async getPaymentStatus(amortizationId: number): Promise<WompiApiResponse> {
    try {
      const response = await request<WompiApiResponse>(
        `${BASE_URL}/api/WompiPayment/payment-status/${amortizationId}`,
        {
          method: "GET"
        }
      );

      return response;
    } catch (error: unknown) {
      console.error(`Error getting payment status for amortization ${amortizationId}:`, error);
      throw error;
    }
  }
}
